/**
 * Onyx journal mobile shop bar.
 *
 * Shows a slim product bar once the article title has scrolled away, and hides
 * it while the in-article module, the end-of-article module (and everything
 * after it), the site footer or Shopify's cookie banner is on screen.
 * Dismissal lasts for the browser session. The bar is fixed and moves with transform only, so it never shifts
 * the page; motion is CSS-only and switched off for reduced-motion users.
 */

const BAR_SELECTOR = '[data-onyx-guide-bar]';
const STORAGE_KEY = 'onyx-guide-bar-dismissed';
const CONSENT_SELECTORS = ['#shopify-pc__banner', '#shopify-pc__prefs'];

/** @returns {boolean} */
function wasDismissed() {
  try {
    return window.sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch (error) {
    return false;
  }
}

function rememberDismissal() {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, '1');
  } catch (error) {
    /* Storage can be blocked; dismissal then lasts for this page view. */
  }
}

/**
 * @param {Element | null} element
 * @returns {boolean} True when the element is rendered and has area on screen.
 */
function isShown(element) {
  if (!(element instanceof HTMLElement)) return false;
  const style = window.getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < window.innerHeight;
}

function consentVisible() {
  return CONSENT_SELECTORS.some((selector) => isShown(document.querySelector(selector)));
}

function init() {
  const bar = document.querySelector(BAR_SELECTOR);
  if (!(bar instanceof HTMLElement) || bar.dataset.onyxReady === 'true') return;
  bar.dataset.onyxReady = 'true';

  if (wasDismissed()) {
    bar.remove();
    return;
  }

  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const start = document.querySelector('main h1');
  const endModule = document.querySelector('[data-onyx-guide-end]');
  /** @type {Element[]} */
  const blockers = [
    document.querySelector('[data-onyx-guide-inline]'),
    endModule,
    document.querySelector('main ~ footer') || document.querySelector('footer'),
  ].filter((element) => element instanceof Element);

  const state = {
    pastStart: !start,
    blocked: new Set(),
  };

  let consentTimer = 0;

  const update = () => {
    const show =
      mobileQuery.matches && state.pastStart && state.blocked.size === 0 && !consentVisible();
    const next = show ? 'visible' : 'hidden';
    if (bar.dataset.state !== next) bar.dataset.state = next;
    // Lets the theme lift the Shopify Inbox chat button above the bar.
    if (show) {
      document.documentElement.style.setProperty('--onyx-guide-bar-offset', `${bar.offsetHeight + 8}px`);
      document.documentElement.dataset.onyxGuideBarVisible = 'true';
    } else {
      delete document.documentElement.dataset.onyxGuideBarVisible;
    }

    // While the bar is wanted, re-check for a late cookie banner once a second.
    const wanted = mobileQuery.matches && state.pastStart && state.blocked.size === 0;
    if (wanted && !consentTimer) {
      consentTimer = window.setInterval(update, 1000);
    } else if (!wanted && consentTimer) {
      window.clearInterval(consentTimer);
      consentTimer = 0;
    }
  };

  if (start) {
    new IntersectionObserver((entries) => {
      for (const entry of entries) {
        state.pastStart = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      }
      update();
    }).observe(start);
  }

  if (blockers.length) {
    const blockerObserver = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        // Once the reader has passed the end module, the bar stays away.
        const passedEnd = entry.target === endModule && entry.boundingClientRect.top < 0;
        if (entry.isIntersecting || passedEnd) {
          state.blocked.add(entry.target);
        } else {
          state.blocked.delete(entry.target);
        }
      }
      update();
    });
    blockers.forEach((element) => blockerObserver.observe(element));
  }

  mobileQuery.addEventListener('change', update);
  document.addEventListener('visitorConsentCollected', () => window.setTimeout(update, 300));

  const close = bar.querySelector('[data-onyx-guide-bar-close]');
  close?.addEventListener('click', () => {
    rememberDismissal();
    if (consentTimer) window.clearInterval(consentTimer);
    bar.dataset.state = 'hidden';
    delete document.documentElement.dataset.onyxGuideBarVisible;
    const main = document.querySelector('main');
    if (main instanceof HTMLElement && bar.contains(document.activeElement)) {
      if (!main.hasAttribute('tabindex')) main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
    window.setTimeout(() => bar.remove(), 400);
  });

  update();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
